import React, { useState } from "react";
import { ArrowUpRight, Shield, Users, Rocket, Heart, Mail, X, Copy, Check } from "lucide-react";
import { JHALA_GLOBAL_CSS, TopStrip, Header, Footer, Reveal, TagChip } from "components/jhala/Layout.js";

const HR_EMAIL = "sales@jhalatech.com";

const JOBS = [
  {
    role: "Shopify Developer",
    tag: "#SHOPIFY",
    type: "Full-time · Remote",
    desc: "Build and customize Shopify Plus stores — theme development, app integrations, and storefront performance for growing ecommerce brands.",
    skills: ["Liquid", "JavaScript", "Shopify API", "Theme Customization"],
  },
  {
    role: "Frontend Developer",
    tag: "#FRONTEND",
    type: "Full-time · Remote",
    desc: "Build responsive, accessible interfaces with React and Next.js for client storefronts and web platforms.",
    skills: ["React", "Next.js", "TypeScript", "Tailwind / CSS"],
  },
  {
    role: "Backend Developer",
    tag: "#BACKEND",
    type: "Full-time · Remote",
    desc: "Design and build REST & GraphQL APIs, database schemas, and server-side logic for client platforms.",
    skills: ["Node.js", "Express", "REST / GraphQL", "MongoDB / SQL"],
  },
  {
    role: "Node.js / AWS Developer",
    tag: "#NODE-AWS",
    type: "Full-time · Remote",
    desc: "Build scalable backend services on Node.js and deploy and manage infrastructure on AWS for client applications.",
    skills: ["Node.js", "AWS (EC2, Lambda, S3)", "CI/CD", "Docker"],
  },
  {
    role: "Python Developer",
    tag: "#PYTHON",
    type: "Full-time · Remote",
    desc: "Build backend services, automation scripts, and data pipelines in Python for internal tools and client integrations.",
    skills: ["Python", "Django / FastAPI", "REST APIs", "SQL"],
  },
  {
    role: "DevOps Engineer",
    tag: "#DEVOPS",
    type: "Full-time · Remote",
    desc: "Own CI/CD pipelines, containerization, and server infrastructure so client releases ship smoothly and reliably.",
    skills: ["Docker & Kubernetes", "CI/CD", "AWS / GCP", "Monitoring"],
  },
];

const VALUES = [
  { icon: Shield, title: "Craft over shortcuts", desc: "Clean code and thoughtful design — not just \"it works.\"" },
  { icon: Users, title: "Communication first", desc: "You always know exactly where a project stands." },
  { icon: Rocket, title: "Built to last", desc: "We build for where the client is going, not just where they are today." },
  { icon: Heart, title: "Partnership, not transactions", desc: "Long-term relationships over one-off gigs." },
];

function ApplyModal({ job, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(HR_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!job) return null;

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(10,15,30,0.75)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#F6F4EE", borderRadius: 10, maxWidth: 440, width: "100%", padding: "32px 28px", position: "relative" }}
      >
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, background: "#0A0F1E", border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <X size={16} color="#F6F4EE" />
        </button>

        <TagChip tone="signal">{job.tag}</TagChip>
        <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 20, fontWeight: 700, margin: "14px 0 8px" }}>
          Apply for {job.role}
        </h3>
        <p style={{ color: "#4A5468", fontSize: 14, lineHeight: 1.6, margin: "0 0 22px" }}>
          Send your resume and portfolio to the email below — mention the role in your subject line
          and our HR team will get back to you.
        </p>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, border: "1px solid #DCD8CC", borderRadius: 6, padding: "12px 14px", background: "#FFFFFF", marginBottom: 16 }}>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 14, color: "#0A0F1E" }}>{HR_EMAIL}</span>
          <button
            onClick={handleCopy}
            className="cta-btn"
            style={{
              background: copied ? "#4CE0B3" : "#0A0F1E", color: copied ? "#0A0F1E" : "#F6F4EE",
              border: "none", borderRadius: 4, padding: "7px 12px", fontSize: 12.5, fontWeight: 600,
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
            }}
          >
            {copied ? (<><Check size={13} /> Copied</>) : (<><Copy size={13} /> Copy</>)}
          </button>
        </div>

        <a
          href={`mailto:${HR_EMAIL}?subject=${encodeURIComponent(`Application: ${job.role}`)}`}
          className="cta-btn"
          style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#4CE0B3", color: "#0A0F1E", textDecoration: "none", padding: "11px 18px", borderRadius: 4, fontWeight: 600, fontSize: 13.5 }}
        >
          Open in Email App <ArrowUpRight size={15} />
        </a>
      </div>
    </div>
  );
}

export default function CareersPage() {
  const [selectedJob, setSelectedJob] = useState(null);

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#F6F4EE", color: "#0A0F1E" }}>
      <style>{JHALA_GLOBAL_CSS}</style>
      <TopStrip />
      <Header />

      {/* HERO */}
      <section style={{ background: "#0A0F1E", color: "#F6F4EE", padding: "64px 24px 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(#23304A 1px, transparent 1px), linear-gradient(90deg, #23304A 1px, transparent 1px)", backgroundSize: "48px 48px", opacity: 0.2, maskImage: "radial-gradient(ellipse at 70% 30%, black, transparent 70%)" }} />
        <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative" }}>
          <Reveal>
            <TagChip>$ jhalatech --careers</TagChip>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(30px, 4.4vw, 46px)", lineHeight: 1.1, margin: "18px 0 14px" }}>
              Join the team.
            </h1>
            <p style={{ fontSize: 16, color: "#B8BFCF", maxWidth: 560, lineHeight: 1.65 }}>
              We're a small, growing team of Shopify specialists and full-stack engineers based in
              Indore, working with clients worldwide.
            </p>
          </Reveal>
        </div>
      </section>

      {/* OPEN POSITIONS */}
      <section style={{ padding: "70px 24px 40px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <TagChip tone="signal">#OPEN-POSITIONS</TagChip>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#8B93A7" }}>01 / current openings</span>
            </div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 700, margin: "0 0 14px" }}>
              Open positions
            </h2>
            <p style={{ color: "#4A5468", fontSize: 15.5, maxWidth: 620, lineHeight: 1.7, marginBottom: 40 }}>
              We're hiring across engineering. Don't see an exact fit? Apply anyway — we're always
              interested in strong developers.
            </p>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18 }}>
            {JOBS.map((job, i) => (
              <Reveal key={job.role} delay={i * 0.06}>
                <div className="svc-card" style={{ border: "1px solid #DCD8CC", borderRadius: 8, padding: "22px 20px", background: "#FFFFFF", height: "100%", display: "flex", flexDirection: "column" }}>
                  <div style={{ marginBottom: 10 }}><TagChip>{job.tag}</TagChip></div>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16.5, fontWeight: 600, margin: "0 0 4px" }}>{job.role}</h3>
                  <p style={{ color: "#FF5C39", fontSize: 12.5, fontWeight: 500, margin: "0 0 10px" }}>{job.type}</p>
                  <p style={{ color: "#6B7280", fontSize: 13.5, lineHeight: 1.6, margin: "0 0 14px" }}>{job.desc}</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
                    {job.skills.map((s) => (
                      <span key={s} style={{ fontSize: 11.5, color: "#4A5468", background: "#F6F4EE", border: "1px solid #DCD8CC", borderRadius: 4, padding: "3px 8px" }}>{s}</span>
                    ))}
                  </div>
                  <button
                    onClick={() => setSelectedJob(job)}
                    className="cta-btn"
                    style={{ marginTop: "auto", background: "#0A0F1E", color: "#F6F4EE", border: "none", padding: "10px 16px", borderRadius: 4, fontWeight: 600, fontSize: 13.5, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                  >
                    Apply <ArrowUpRight size={14} />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section style={{ padding: "50px 24px 90px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#8B93A7" }}>02 / what we look for</span>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 2.8vw, 30px)", fontWeight: 700, margin: "10px 0 36px" }}>
              How we work
            </h2>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 18 }}>
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <div className="value-card" style={{ border: "1px solid #DCD8CC", borderRadius: 8, padding: "26px 22px", background: "#FFFFFF", height: "100%" }}>
                  <v.icon size={22} className="icon-pop" style={{ color: "#FF5C39", marginBottom: 14 }} />
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600, margin: "0 0 8px" }}>{v.title}</h3>
                  <p style={{ color: "#6B7280", fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "70px 24px", background: "#0A0F1E", color: "#F6F4EE", textAlign: "center" }}>
        <Reveal>
          <Mail size={26} style={{ color: "#4CE0B3", marginBottom: 16 }} />
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 700, margin: "0 0 14px" }}>
            Don't see your role?
          </h2>
          <p style={{ color: "#B8BFCF", fontSize: 15, marginBottom: 26 }}>
            Send your resume or portfolio and a note about what you'd want to work on.
          </p>
          <a href={`mailto:${HR_EMAIL}?subject=Career%20Interest`} className="cta-btn" style={{ background: "#4CE0B3", color: "#0A0F1E", border: "none", padding: "13px 24px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
            Email {HR_EMAIL} <ArrowUpRight size={16} />
          </a>
        </Reveal>
      </section>

      <Footer />

      {selectedJob && <ApplyModal job={selectedJob} onClose={() => setSelectedJob(null)} />}
    </div>
  );
}
